import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import GuidanceModeSelector from '../Chat/GuidanceModeSelector';
import { sessionsApi } from '../../api/sessions';
import FrameworkPicker from '../Frameworks/FrameworkPicker';
import { storyFrameworkRef } from '../../api/frameworks';
import { storiesApi } from '../../api/stories';
import { PLOT_DOOR_LABEL, focusColumnLabel } from '../../utils/focusTemplates';
import { NAV_WHY_OPEN_BOOK } from '../../utils/navDisabledWhy';
import {
  EXPLORE_MODE_EXAMPLES,
  EXPLORE_MODE_HELP,
  EXPLORE_MODE_NAME,
  EXPLORE_MODE_SHORT,
  FOCUS_MODE_HELP,
  FOCUS_MODE_NAME,
  FOCUS_MODE_SHORT,
  SESSION_FRAMEWORK_HINT,
  STAYS_IN_BOOK,
} from '../../utils/writerCopy';
import './NewSessionDialog.css';

/** Same defaulting as Book Notes Character Focus, and as the server
 *  when no name is sent: "Character: Clara" / "Character: unnamed" /
 *  "The book's argument" at book, "Series Theme" at series. */
export function defaultFocusSessionTitle(template, targetName, customName, seriesAltitude = false) {
  const key = (template || '').trim();
  const label = (key === 'custom' && (customName || '').trim())
    ? customName.trim()
    : focusColumnLabel(key, seriesAltitude);
  const handle = (targetName || '').trim();
  if (handle) return `${label}: ${handle}`;
  if (key === 'character') return `${label}: unnamed`;
  if (key === 'plot') return PLOT_DOOR_LABEL;
  if (key === 'series_theme') return label;
  if (seriesAltitude && key === 'world') return label;
  return `${label} Development`;
}

/**
 * NewSessionDialog - Dialog for creating new sessions with guidance mode selection
 *
 * Props:
 * - storyId: UUID of the story to create the session for
 * - onClose: Callback when dialog is closed
 * - onSessionCreated: Callback with created session data
 */
export default function NewSessionDialog({
  storyId,
  projectId: projectIdProp,
  seriesAltitude = false,
  onClose,
  onSessionCreated,
  initialPrimaryMode = 'explore',
  initialFocusTemplate = null,
  storyName = '',
}) {
  // Primary mode: Explore or Focus + live subtypes, inside the open book.
  // Never Discovery, and never a new story (#1226).
  const [primaryMode, setPrimaryMode] = useState(initialPrimaryMode);

  // Focus state
  const [focusTemplate, setFocusTemplate] = useState(initialFocusTemplate);
  const [focusTargetName, setFocusTargetName] = useState('');
  const [existingPlotSession, setExistingPlotSession] = useState(null);
  const [plotPickerOpen, setPlotPickerOpen] = useState(false);
  const [plotFramework, setPlotFramework] = useState(null);
  const [plotDeclined, setPlotDeclined] = useState(false);
  const [customFocus, setCustomFocus] = useState(null);  // Custom focus support
  const [projectId, setProjectId] = useState(projectIdProp || null);

  const [bookName, setBookName] = useState((storyName || '').trim());
  const [sessionTitle, setSessionTitle] = useState('');
  const [titleDirty, setTitleDirty] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  // Series create must be ready on the first paint. Starting in Loading
  // disabled Create Session for one frame — the first confirm no-op'd.
  const [isLoading, setIsLoading] = useState(
    Boolean(storyId) && !(seriesAltitude && projectIdProp),
  );

  const focusTitleDefault = defaultFocusSessionTitle(
    focusTemplate,
    focusTargetName,
    customFocus?.name,
    seriesAltitude,
  );

  useEffect(() => {
    if (primaryMode !== 'focus' || !(focusTemplate || customFocus)) return;
    if (!titleDirty) setSessionTitle(focusTitleDefault);
  }, [primaryMode, focusTemplate, focusTargetName, customFocus, titleDirty, focusTitleDefault]);

  useEffect(() => {
    const checkStoryState = async () => {
      setIsLoading(true);
      try {
        if (seriesAltitude && projectIdProp) {
          setProjectId(projectIdProp);
          setExistingPlotSession(null);
          setIsLoading(false);
          return;
        }
        const sessionsResponse = await sessionsApi.list(storyId);
        const sessions = sessionsResponse?.data || sessionsResponse || [];
        const plotSession = sessions.find(
          s => s.guidance_mode === 'focus' && s.template === 'plot'
        );
        setExistingPlotSession(plotSession || null);

        if (sessions.length > 0 && sessions[0].project_id) {
          setProjectId(sessions[0].project_id);
        } else if (projectIdProp) {
          setProjectId(projectIdProp);
        }
        if (!bookName && storyId) {
          const storyResponse = await storiesApi.get(storyId);
          const story = storyResponse?.data || storyResponse;
          if (story?.name) setBookName(story.name);
          if (story?.project_id && !(sessions.length > 0 && sessions[0].project_id) && !projectIdProp) {
            setProjectId(story.project_id);
          }
        }
      } catch (err) {
        console.error('Failed to check story state:', err);
        setExistingPlotSession(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (storyId || (seriesAltitude && projectIdProp)) {
      checkStoryState();
    } else {
      setIsLoading(false);
    }
  }, [storyId, seriesAltitude, projectIdProp]);

  const inBook = Boolean(storyId);
  const seriesSession = Boolean(seriesAltitude && (projectId || projectIdProp) && !storyId);
  const canCreate = (inBook || seriesSession) && (
    primaryMode === 'explore' ||
    (primaryMode === 'focus' && (!!focusTemplate || !!customFocus))
  );

  const handleCreate = async () => {
    if (!canCreate || isCreating) return;

    setIsCreating(true);
    setError(null);

    try {
      let session;

      if (primaryMode === 'focus' && focusTemplate === 'plot' && existingPlotSession && !seriesAltitude) {
        onSessionCreated(existingPlotSession);
        onClose();
        return;
      } else {
        const sessionData = {
          guidance_mode: primaryMode,
          template: primaryMode === 'focus' ? focusTemplate : 'open',
          focus_target_name: focusTargetName || null,
        };
        if (primaryMode === 'focus') {
          sessionData.name = (sessionTitle.trim() || focusTitleDefault);
        }
        if (primaryMode === 'focus' && focusTemplate === 'plot' && !seriesAltitude) {
          if (plotFramework) {
            sessionData.plot_framework_ref = storyFrameworkRef(plotFramework);
          } else if (plotDeclined) {
            sessionData.plot_framework_declined = true;
          }
        }

        if (primaryMode === 'focus' && focusTemplate === 'custom' && customFocus) {
          sessionData.custom_focus_id = customFocus.id;
        }

        const scopeProjectId = projectId || projectIdProp;
        if (storyId) {
          const response = await sessionsApi.create(storyId, sessionData);
          session = response?.data || response;
        } else if (seriesAltitude && scopeProjectId) {
          // Series Focus offer: a series sitting, not a new story.
          const response = await sessionsApi.createProject(scopeProjectId, sessionData);
          session = response?.data || response;
        } else {
          setError(NAV_WHY_OPEN_BOOK);
          return;
        }
      }

      onSessionCreated(session);
      onClose();
    } catch (err) {
      console.error('Failed to create session:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create session';
      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDismiss = (e) => {
    e?.preventDefault?.();
    if (isCreating) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (plotPickerOpen || isCreating) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isCreating, plotPickerOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleDismiss(e);
    }
  };

  return createPortal(
    <div
      className="new-session-dialog-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-session-dialog-title"
    >
      <div className="new-session-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="new-session-dialog-header">
          <h2 id="new-session-dialog-title">Create New Session</h2>
          <button
            type="button"
            className="new-session-dialog-close"
            onClick={handleDismiss}
            disabled={isCreating}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className="new-session-dialog-content">
          {isLoading ? (
            <div className="new-session-dialog-loading">
              <div className="loading-spinner"></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="new-session-dialog-error">
                  {error}
                </div>
              )}

              {!inBook && !seriesSession && (
                <p className="new-session-dialog-hint new-session-dialog-lead">
                  {NAV_WHY_OPEN_BOOK}
                </p>
              )}

              {(inBook || seriesSession) && (
              <>
              {inBook && (
                <p className="new-session-dialog-hint new-session-dialog-lead">
                  {bookName ? `This stays in ${bookName}.` : STAYS_IN_BOOK}
                </p>
              )}
              <div className="primary-mode-selector">
                <button
                  type="button"
                  className={`primary-mode-btn ${primaryMode === 'explore' ? 'active' : ''}`}
                  onClick={() => setPrimaryMode('explore')}
                >
                  <span className="primary-mode-name">{EXPLORE_MODE_NAME}</span>
                  <span className="primary-mode-recommend">Recommended</span>
                  <span className="primary-mode-desc">{EXPLORE_MODE_SHORT}</span>
                </button>
                <button
                  type="button"
                  className={`primary-mode-btn ${primaryMode === 'focus' ? 'active' : ''}`}
                  onClick={() => setPrimaryMode('focus')}
                >
                  <span className="primary-mode-name">{FOCUS_MODE_NAME}</span>
                  <span className="primary-mode-desc">{FOCUS_MODE_SHORT}</span>
                </button>
              </div>
              <p className="new-session-dialog-hint" id="explore-focus-help">
                {primaryMode === 'explore' ? EXPLORE_MODE_HELP : FOCUS_MODE_HELP}
              </p>
              {primaryMode === 'explore' && (
                <p className="new-session-dialog-hint">{EXPLORE_MODE_EXAMPLES}</p>
              )}

              {/* Focus template options */}
              {primaryMode === 'focus' && (
                <GuidanceModeSelector
                  mode="focus"
                  onModeChange={() => {}}
                  template={focusTemplate}
                  onTemplateChange={setFocusTemplate}
                  focusTargetName={focusTargetName}
                  onFocusTargetNameChange={setFocusTargetName}
                  hideModePicker={true}
                  storyId={seriesAltitude ? null : storyId}
                  projectId={projectId || projectIdProp}
                  customFocus={customFocus}
                  onCustomFocusChange={setCustomFocus}
                  useNewFocusPicker={true}
                  seriesAltitude={seriesAltitude}
                />
              )}

              {primaryMode === 'focus' && (focusTemplate || customFocus) && (
                <div className="focus-target-input">
                  <label htmlFor="new-session-title">Session title</label>
                  <input
                    id="new-session-title"
                    type="text"
                    value={sessionTitle}
                    onChange={(e) => {
                      setTitleDirty(true);
                      setSessionTitle(e.target.value);
                    }}
                    placeholder={focusTitleDefault}
                  />
                  <p className="new-session-dialog-hint">
                    Optional. You can rename this later from the session list (double-click or right-click).
                  </p>
                </div>
              )}

              {primaryMode === 'focus' && focusTemplate === 'plot' && existingPlotSession && !seriesAltitude && (
                <p className="new-session-dialog-hint">
                  This book already has a session about the book&apos;s argument. Opening it keeps that conversation.
                </p>
              )}

              {primaryMode === 'focus' && focusTemplate === 'plot' && !existingPlotSession && !seriesAltitude && (
                <div className="focus-target-input">
                  <label>Framework lens (optional)</label>
                  <p className="new-session-dialog-hint">
                    {SESSION_FRAMEWORK_HINT}
                  </p>
                  <button
                    type="button"
                    className="new-session-dialog-cancel-btn"
                    onClick={() => setPlotPickerOpen(true)}
                  >
                    {plotFramework ? plotFramework.framework_name : 'Choose a Framework'}
                  </button>
                  {plotFramework && (
                    <button
                      type="button"
                      className="new-session-dialog-cancel-btn"
                      onClick={() => { setPlotFramework(null); setPlotDeclined(true); }}
                    >
                      Continue without one
                    </button>
                  )}
                </div>
              )}
              </>
              )}

            </>
          )}
        </div>

        <div className="new-session-dialog-actions">
          <button
            type="button"
            className="new-session-dialog-cancel-btn"
            onClick={handleDismiss}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            type="button"
            className="new-session-dialog-create-btn"
            onClick={handleCreate}
            disabled={!canCreate || isCreating || isLoading}
          >
            {isCreating
              ? 'Creating...'
              : (primaryMode === 'focus' && focusTemplate === 'plot' && existingPlotSession && !seriesAltitude
                ? `Open ${PLOT_DOOR_LABEL}`
                : 'Create Session')}
          </button>
        </div>
      </div>
      <FrameworkPicker
        isOpen={plotPickerOpen}
        onClose={() => setPlotPickerOpen(false)}
        mode="select"
        hangKind="session"
        selectHint={SESSION_FRAMEWORK_HINT}
        onSelect={(dto) => {
          setPlotFramework(dto);
          setPlotDeclined(false);
          setPlotPickerOpen(false);
        }}
      />
    </div>,
    document.body
  );
}

// Named export for flexibility
export { NewSessionDialog };
